/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * What the eight Trends sections share (docs/CONTRACT-fleet-governance.md section 12.3): their props (the range and app,
 * and the way to open "Show audit rows"), the Show audit rows button, a failed load in words, and client-side sorting for
 * the tables the service answers whole (every table sorts; the DLP and funnel tables sort and page on the service).
 */
import { useState } from 'react';
import { ScrollText } from 'lucide-react';
import { trendsFailure, type DrillRequest, type TrendsFilter } from '../../lib/trends';
import { Button, type TableSort } from '../../ui';
import { LoadError } from '../fleet/parts';

export interface SectionProps {
  filter: TrendsFilter;
  /** The section routes' query: the range and the app. */
  query: Record<string, string | number | undefined>;
  onShowRows: (request: DrillRequest) => void;
}

/** "Show audit rows": opens the drill-down for a number. */
export function ShowRows({ onClick, label = 'Show audit rows' }: { onClick: () => void; label?: string }) {
  return <Button variant="ghost" size="sm" icon={ScrollText} onClick={onClick}>{label}</Button>;
}

export function SectionError({ title, error, onRetry, retrying }: { title: string; error: unknown; onRetry?: () => void; retrying?: boolean }) {
  return <LoadError title={title} error={error} words={(failure) => trendsFailure(failure)} onRetry={onRetry} retrying={retrying} />;
}

type SortValue = string | number | null | undefined;

/**
 * Sorting on the client for a table the service answers whole: no column is sorted until a header is clicked (the
 * answer's own order stands); a click sorts by that column, numbers descending first, words ascending first.
 */
export function useClientSort<T>(rows: readonly T[], value: (row: T, key: string) => SortValue, words: readonly string[] = []): { rows: T[]; sort: TableSort } {
  const [state, setState] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const sorted = state ? [...rows].sort((a, b) => {
    const x = value(a, state.key);
    const y = value(b, state.key);
    const sign = state.direction === 'asc' ? 1 : -1;
    if (x === y) return 0;
    if (x === null || x === undefined) return 1;
    if (y === null || y === undefined) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sign;
    return String(x).localeCompare(String(y), 'en-US', { sensitivity: 'base' }) * sign;
  }) : [...rows];
  const sort: TableSort = {
    key: state?.key ?? '',
    direction: state?.direction ?? 'desc',
    onSort: (key) => setState((current) => (current && current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: words.includes(key) ? 'asc' : 'desc' })),
  };
  return { rows: sorted, sort };
}
